import React, { memo, useState, useEffect, useMemo } from 'react';
import styled from 'styled-components';
import {
  Popover,
  Select,
  Input,
  InputNumber,
  DatePicker,
  Button,
  Tooltip,
  Divider,
} from 'antd';
import {
  FilterOutlined,
  FilterFilled,
  CaretUpOutlined,
  CaretDownOutlined,
  SortAscendingOutlined,
  SortDescendingOutlined,
  ClearOutlined,
  CheckOutlined,
  PushpinFilled,
} from '@ant-design/icons';
import dayjs from 'dayjs';
import qs from 'qs';
import {
  ColumnFilterFieldType,
  ColumnFilterRule,
  ColumnSortRule,
  StrapiFilterOperator,
  STRAPI_OPERATOR_LABELS,
  isColumnFilterRuleActive,
} from '~/utils/strapiFilters';

const { RangePicker } = DatePicker;

export interface SelectOptionItem {
  label: string;
  value: string | number;
}

interface ColumnHeaderControlProps {
  columnKey: string;
  label: string;
  titleNode: React.ReactNode;
  isSystemColumn?: boolean;
  isPinned?: boolean;
  fieldType?: ColumnFilterFieldType;
  selectOptions?: SelectOptionItem[];
  activeFilter?: ColumnFilterRule;
  activeSort?: ColumnSortRule | null;
  onApplyFilter: (columnKey: string, rule: ColumnFilterRule | null) => void;
  onChangeSort: (sort: ColumnSortRule | null) => void;
}

const OPERATORS_BY_TYPE: Record<ColumnFilterFieldType, StrapiFilterOperator[]> = {
  text: [
    '$containsi',
    '$notContainsi',
    '$eq',
    '$eqi',
    '$ne',
    '$startsWithi',
    '$endsWithi',
    '$in',
    '$notIn',
    '$null',
    '$notNull',
  ],
  select: [
    '$eq',
    '$ne',
    '$in',
    '$notIn',
    '$containsi',
    '$null',
    '$notNull',
  ],
  number: [
    '$eq',
    '$ne',
    '$gt',
    '$gte',
    '$lt',
    '$lte',
    '$between',
    '$null',
    '$notNull',
  ],
  date: [
    '$between',
    '$gte',
    '$lte',
    '$eq',
    '$null',
    '$notNull',
  ],
  boolean: [
    '$eq',
    '$null',
    '$notNull',
  ],
  user: [
    '$in',
    '$notIn',
    '$eq',
  ],
};

const DEFAULT_OPERATOR_BY_TYPE: Record<ColumnFilterFieldType, StrapiFilterOperator> = {
  text: '$containsi',
  select: '$eq',
  number: '$eq',
  date: '$between',
  boolean: '$eq',
  user: '$in',
};

const ColumnHeaderControl: React.FC<ColumnHeaderControlProps> = ({
  columnKey,
  label,
  titleNode,
  isSystemColumn = false,
  isPinned = false,
  fieldType = 'text',
  selectOptions = [],
  activeFilter,
  activeSort,
  onApplyFilter,
  onChangeSort,
}) => {
  const [open, setOpen] = useState(false);
  const availableOperators = OPERATORS_BY_TYPE[fieldType] || OPERATORS_BY_TYPE.text;
  const defaultOp = DEFAULT_OPERATOR_BY_TYPE[fieldType] || '$containsi';

  const [operator, setOperator] = useState<StrapiFilterOperator>(
    activeFilter?.operator || defaultOp,
  );
  const [value, setValue] = useState<any>(activeFilter?.value ?? undefined);

  useEffect(() => {
    if (open) {
      setOperator(activeFilter?.operator || defaultOp);
      setValue(activeFilter?.value ?? undefined);
    }
  }, [open, activeFilter, defaultOp]);

  const isFiltered = isColumnFilterRuleActive(activeFilter);
  const currentSortDir =
    activeSort?.columnKey === columnKey ? activeSort.direction : undefined;

  const handleCycleSort = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!currentSortDir) {
      onChangeSort({ columnKey, label, isSystemColumn, direction: 'asc' });
    } else if (currentSortDir === 'asc') {
      onChangeSort({ columnKey, label, isSystemColumn, direction: 'desc' });
    } else {
      onChangeSort(null);
    }
  };

  const handleSetSort = (dir: 'asc' | 'desc') => {
    if (currentSortDir === dir) {
      onChangeSort(null);
    } else {
      onChangeSort({ columnKey, label, isSystemColumn, direction: dir });
    }
  };

  const handleOperatorChange = (nextOp: StrapiFilterOperator) => {
    setOperator(nextOp);
    if (nextOp === '$null' || nextOp === '$notNull') {
      setValue(true);
    } else if (nextOp === '$between') {
      setValue([undefined, undefined]);
    } else if (nextOp === '$in' || nextOp === '$notIn') {
      setValue(Array.isArray(value) ? value : []);
    } else if (Array.isArray(value)) {
      setValue(undefined);
    }
  };

  const handleApply = () => {
    const candidateRule: ColumnFilterRule = {
      columnKey,
      label,
      isSystemColumn,
      fieldType,
      operator,
      value: operator === '$null' || operator === '$notNull' ? true : value,
    };

    if (isColumnFilterRuleActive(candidateRule)) {
      onApplyFilter(columnKey, candidateRule);
    } else {
      onApplyFilter(columnKey, null);
    }
    setOpen(false);
  };

  const handleClear = () => {
    setOperator(defaultOp);
    setValue(undefined);
    onApplyFilter(columnKey, null);
    setOpen(false);
  };

  // Build live Strapi query preview for this column
  const strapiSnippetPreview = useMemo(() => {
    const previewObj: Record<string, any> = {};
    const valPreview =
      operator === '$null' || operator === '$notNull'
        ? true
        : value !== undefined && value !== ''
          ? value
          : '<value>';

    if (columnKey === 'user' || fieldType === 'user') {
      previewObj.filters = { user: { id: { [operator]: valPreview } } };
    } else if (isSystemColumn) {
      previewObj.filters = { [columnKey]: { [operator]: valPreview } };
    } else {
      previewObj.filters = { properties: { [columnKey]: { [operator]: valPreview } } };
    }

    return qs.stringify(previewObj, { encodeValuesOnly: true });
  }, [columnKey, fieldType, isSystemColumn, operator, value]);

  const renderValueInput = () => {
    if (operator === '$null' || operator === '$notNull') {
      return (
        <NullHintBox>
          {operator === '$null'
            ? 'Sẽ lọc các bản ghi có trường này đang trống hoặc null.'
            : 'Sẽ lọc các bản ghi có trường này đã được nhập dữ liệu (khác null/trống).'}
        </NullHintBox>
      );
    }

    if (fieldType === 'boolean') {
      return (
        <Select
          size="small"
          style={{ width: '100%' }}
          placeholder="Chọn giá trị..."
          value={value}
          onChange={(val) => setValue(val)}
          options={[
            { label: '✓ Có (true)', value: 'true' },
            { label: '✗ Không (false)', value: 'false' },
          ]}
          allowClear
        />
      );
    }

    if (fieldType === 'user') {
      return (
        <Select
          size="small"
          mode={operator === '$eq' ? undefined : 'multiple'}
          style={{ width: '100%' }}
          placeholder="Chọn người tạo..."
          value={value}
          onChange={(val) => setValue(val)}
          options={selectOptions}
          showSearch
          filterOption={(input, opt) =>
            String(opt?.label ?? '')
              .toLowerCase()
              .includes(input.toLowerCase())
          }
          allowClear
          maxTagCount="responsive"
        />
      );
    }

    if (fieldType === 'number') {
      if (operator === '$between') {
        const minVal = Array.isArray(value) ? value[0] : undefined;
        const maxVal = Array.isArray(value) ? value[1] : undefined;
        return (
          <BetweenRow>
            <InputNumber
              size="small"
              style={{ flex: 1 }}
              placeholder="Từ (Min)"
              value={minVal}
              onChange={(v) => setValue([v, maxVal])}
              onPressEnter={handleApply}
            />
            <span>~</span>
            <InputNumber
              size="small"
              style={{ flex: 1 }}
              placeholder="Đến (Max)"
              value={maxVal}
              onChange={(v) => setValue([minVal, v])}
              onPressEnter={handleApply}
            />
          </BetweenRow>
        );
      }

      return (
        <InputNumber
          size="small"
          style={{ width: '100%' }}
          placeholder="Nhập giá trị số..."
          value={value}
          onChange={(v) => setValue(v)}
          onPressEnter={handleApply}
        />
      );
    }

    if (fieldType === 'date') {
      if (operator === '$between') {
        const rangeVal =
          Array.isArray(value) && value[0] && value[1]
            ? ([dayjs(value[0]), dayjs(value[1])] as [dayjs.Dayjs, dayjs.Dayjs])
            : null;
        return (
          <RangePicker
            size="small"
            style={{ width: '100%' }}
            placeholder={['Từ ngày', 'Đến ngày']}
            value={rangeVal}
            onChange={(dates) => {
              if (dates && dates[0] && dates[1]) {
                setValue([
                  dates[0].startOf('day').toISOString(),
                  dates[1].endOf('day').toISOString(),
                ]);
              } else {
                setValue(undefined);
              }
            }}
          />
        );
      }

      return (
        <DatePicker
          size="small"
          style={{ width: '100%' }}
          placeholder="Chọn ngày..."
          value={value ? dayjs(value) : null}
          onChange={(date) => {
            if (!date) {
              setValue(undefined);
              return;
            }
            if (operator === '$lte') {
              setValue(date.endOf('day').toISOString());
            } else {
              setValue(date.startOf('day').toISOString());
            }
          }}
        />
      );
    }

    if (
      fieldType === 'select' &&
      selectOptions.length > 0 &&
      (operator === '$eq' || operator === '$ne' || operator === '$in' || operator === '$notIn')
    ) {
      return (
        <Select
          size="small"
          mode={operator === '$in' || operator === '$notIn' ? 'multiple' : undefined}
          style={{ width: '100%' }}
          placeholder="Chọn giá trị..."
          value={value}
          onChange={(val) => setValue(val)}
          options={selectOptions}
          showSearch
          allowClear
          maxTagCount="responsive"
        />
      );
    }

    if (operator === '$in' || operator === '$notIn') {
      return (
        <Select
          size="small"
          mode="tags"
          style={{ width: '100%' }}
          placeholder="Nhập giá trị và nhấn Enter..."
          value={Array.isArray(value) ? value : []}
          onChange={(val) => setValue(val)}
          options={selectOptions}
          allowClear
        />
      );
    }

    return (
      <Input
        size="small"
        placeholder={`Nhập giá trị lọc ${label}...`}
        value={value ?? ''}
        onChange={(e) => setValue(e.target.value)}
        onPressEnter={handleApply}
        allowClear
      />
    );
  };

  const popoverContent = (
    <PopoverContainer
      data-testid={`column-filter-popover-${columnKey}`}
      onClick={(e) => e.stopPropagation()}
    >
      <PopoverHeader>
        <PopoverTitle>{label}</PopoverTitle>
        <FieldPathBadge>
          {isSystemColumn ? columnKey : `properties.${columnKey}`}
        </FieldPathBadge>
      </PopoverHeader>

      <SectionBlock>
        <SectionLabel>Sắp xếp</SectionLabel>
        <SortButtonsRow>
          <SortOptionBtn
            data-testid={`column-sort-asc-${columnKey}`}
            $active={currentSortDir === 'asc'}
            onClick={() => handleSetSort('asc')}
          >
            <SortAscendingOutlined />
            <span>Tăng dần (ASC)</span>
          </SortOptionBtn>
          <SortOptionBtn
            data-testid={`column-sort-desc-${columnKey}`}
            $active={currentSortDir === 'desc'}
            onClick={() => handleSetSort('desc')}
          >
            <SortDescendingOutlined />
            <span>Giảm dần (DESC)</span>
          </SortOptionBtn>
        </SortButtonsRow>
      </SectionBlock>

      <Divider style={{ margin: '8px 0' }} />

      <SectionBlock>
        <SectionLabel>Điều kiện lọc</SectionLabel>
        <Select
          data-testid={`column-operator-select-${columnKey}`}
          size="small"
          style={{ width: '100%' }}
          value={operator}
          onChange={handleOperatorChange}
          options={availableOperators.map((op) => ({
            label: STRAPI_OPERATOR_LABELS[op] || op,
            value: op,
          }))}
        />
        <div style={{ marginTop: 6 }}>{renderValueInput()}</div>
      </SectionBlock>

      <QueryPreviewBox title={strapiSnippetPreview}>
        <code>{strapiSnippetPreview}</code>
      </QueryPreviewBox>

      <PopoverFooter>
        <Button
          data-testid={`column-filter-clear-${columnKey}`}
          size="small"
          icon={<ClearOutlined />}
          onClick={handleClear}
        >
          Hủy lọc
        </Button>
        <Button
          data-testid={`column-filter-apply-${columnKey}`}
          size="small"
          type="primary"
          icon={<CheckOutlined />}
          onClick={handleApply}
        >
          Áp dụng
        </Button>
      </PopoverFooter>
    </PopoverContainer>
  );

  return (
    <HeaderCellWrapper data-testid={`column-header-${columnKey}`}>
      <HeaderLeft onClick={handleCycleSort} title={`Nhấn để sắp xếp theo "${label}"`}>
        {titleNode}
        {isPinned && (
          <PushpinFilled
            style={{ color: '#2563eb', fontSize: 11, flexShrink: 0 }}
            title="Cột đang được ghim cố định"
          />
        )}
      </HeaderLeft>

      <HeaderActions>
        <Tooltip
          title={
            currentSortDir === 'asc'
              ? 'Đang sắp xếp Tăng dần (Nhấn để chuyển sang Giảm dần)'
              : currentSortDir === 'desc'
                ? 'Đang sắp xếp Giảm dần (Nhấn để bỏ sắp xếp)'
                : 'Nhấn để sắp xếp Tăng dần'
          }
        >
          <SortTriggerBtn
            data-testid={`column-sort-btn-${columnKey}`}
            $active={Boolean(currentSortDir)}
            onClick={handleCycleSort}
          >
            <CaretUpOutlined
              style={{
                fontSize: 9,
                lineHeight: 1,
                color: currentSortDir === 'asc' ? '#2563eb' : '#94a3b8',
              }}
            />
            <CaretDownOutlined
              style={{
                fontSize: 9,
                lineHeight: 1,
                marginTop: -2,
                color: currentSortDir === 'desc' ? '#2563eb' : '#94a3b8',
              }}
            />
          </SortTriggerBtn>
        </Tooltip>

        <Popover
          content={popoverContent}
          trigger="click"
          placement="bottomRight"
          open={open}
          onOpenChange={setOpen}
        >
          <Tooltip title="Lọc & sắp xếp theo cột">
            <FilterTriggerBtn
              data-testid={`column-filter-btn-${columnKey}`}
              $active={isFiltered}
              onClick={(e) => e.stopPropagation()}
            >
              {isFiltered ? (
                <FilterFilled style={{ fontSize: 11, color: '#2563eb' }} />
              ) : (
                <FilterOutlined style={{ fontSize: 11 }} />
              )}
            </FilterTriggerBtn>
          </Tooltip>
        </Popover>
      </HeaderActions>
    </HeaderCellWrapper>
  );
};

export default memo(ColumnHeaderControl);

const HeaderCellWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 4px;
  width: 100%;
  user-select: none;
`;

const HeaderLeft = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  min-width: 0;
  flex: 1;
  cursor: pointer;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;

  &:hover {
    color: #2563eb;
  }
`;

const HeaderActions = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 2px;
  flex-shrink: 0;
`;

const SortTriggerBtn = styled.div<{ $active?: boolean }>`
  display: inline-flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  width: 16px;
  height: 20px;
  border-radius: 4px;
  cursor: pointer;
  background: ${(p) => (p.$active ? '#eff6ff' : 'transparent')};
  transition: background 0.15s ease;

  &:hover {
    background: #e2e8f0;
  }
`;

const FilterTriggerBtn = styled.div<{ $active?: boolean }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 20px;
  height: 20px;
  border-radius: 4px;
  cursor: pointer;
  color: ${(p) => (p.$active ? '#2563eb' : '#94a3b8')};
  background: ${(p) => (p.$active ? '#dbeafe' : 'transparent')};
  border: ${(p) => (p.$active ? '1px solid #93c5fd' : '1px solid transparent')};
  transition: all 0.15s ease;

  &:hover {
    color: #2563eb;
    background: #eff6ff;
  }
`;

const PopoverContainer = styled.div`
  width: 280px;
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const PopoverHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  padding-bottom: 6px;
  border-bottom: 1px solid #f1f5f9;
`;

const PopoverTitle = styled.div`
  font-size: 13px;
  font-weight: 600;
  color: #1e293b;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const FieldPathBadge = styled.span`
  font-size: 10px;
  font-family: monospace;
  color: #475569;
  background: #f1f5f9;
  padding: 2px 6px;
  border-radius: 4px;
  border: 1px solid #e2e8f0;
  flex-shrink: 0;
`;

const SectionBlock = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const SectionLabel = styled.div`
  font-size: 11px;
  font-weight: 600;
  color: #64748b;
`;

const SortButtonsRow = styled.div`
  display: flex;
  gap: 6px;
`;

const SortOptionBtn = styled.button<{ $active?: boolean }>`
  flex: 1;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
  padding: 4px 8px;
  font-size: 11px;
  font-weight: 500;
  border-radius: 6px;
  cursor: pointer;
  border: 1px solid ${(p) => (p.$active ? '#3b82f6' : '#e2e8f0')};
  background: ${(p) => (p.$active ? '#eff6ff' : '#ffffff')};
  color: ${(p) => (p.$active ? '#2563eb' : '#475569')};
  transition: all 0.15s ease;

  &:hover {
    border-color: #93c5fd;
    color: #2563eb;
  }
`;

const BetweenRow = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
`;

const NullHintBox = styled.div`
  font-size: 11px;
  color: #64748b;
  background: #f8fafc;
  border: 1px dashed #cbd5e1;
  border-radius: 6px;
  padding: 6px 8px;
`;

const QueryPreviewBox = styled.div`
  margin-top: 2px;
  padding: 4px 8px;
  background: #0f172a;
  color: #38bdf8;
  border-radius: 6px;
  font-size: 10px;
  font-family: monospace;
  overflow-x: auto;
  white-space: nowrap;
`;

const PopoverFooter = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  padding-top: 6px;
  border-top: 1px solid #f1f5f9;
`;
